import { defineStore } from 'pinia'
import type { UserCredentials } from '~/types/userCredentials'
import type { CustomerCredentials } from '~/types/customerCredentials'
import type { LoginResponse } from '~/types/loginResponse'
import type { Tenant } from '~/types/tenant'

export const useAuthStore = defineStore('iqa-auth', {
  state: () => ({
    token: null as string | null,
    refreshToken: null as string | null,
    expiration: null as string | null,
    expiresAt: 0 as number,
    user: null as UserCredentials | CustomerCredentials | null,
    selectedTenant: null as any,
    sessionType: null as null | 'internal' | 'customer',
    customerPermissions: [] as string[],
  }),

  actions: {
    setUser(u: UserCredentials) {
      this.user = u;
    },
    setTenant(u: LoginResponse, tenant:Tenant) {
      if(!u.user.tenants)
      {
        //Opción no válida. Mandar a login.
        this.logout();
        return false;
      }
      //borra caché de catálogos
      localStorage.removeItem('iqa-data-entry-tree-cache'); //catálogo de inspecciones
      localStorage.removeItem('iqa-data-entry-tree-cache-records');
      localStorage.removeItem('iqa-data-entry-tree-cache-rex3');
      localStorage.removeItem('iqa-data-entry-tree-cache-open-cell');
      localStorage.removeItem('iqa-data-entry-tree-cache-batches');

      Object.keys(localStorage)
        .filter(key => key.startsWith('catalogo-'))
        .forEach(key => localStorage.removeItem(key)); //remueve todos los catálogos

      this.selectedTenant = {
          label: tenant.name,
          code: tenant.code,
          value: tenant.id,
          roles: tenant.roles,
          menu: tenant.menu
      };
      this.sessionType = 'internal';
      this.customerPermissions = [];
      if (!u.token || !u.refreshToken || !u.expiration|| !u.accessTokenExpiresIn) {
        this.logout()
        return false
      }
      this.setToken({token:u.token, refreshToken:u.refreshToken, expiration:u.expiration, accessTokenExpiresIn:u.accessTokenExpiresIn});
    },
    unsetTenant() {
      const authStr = localStorage.getItem('iqa-auth')
      if (authStr) {
        const authObj = JSON.parse(authStr)

        // 2. Borrar la propiedad selectedTenant
        delete authObj.selectedTenant

        // 3. Guardar de nuevo el objeto actualizado
        localStorage.setItem('iqa-auth', JSON.stringify(authObj))
      }
    },
    setTempToken(token: string){
        //Guarda el token en cookies, y el resto de la info del usuario en UserStore
        const authToken  = useCookie('iqa-token');
        //const authToken  = useCookie('iqa-token', { maxAge: secondsUntilExpire, secure: true });
        
        this.token = token;
        authToken.value = token;
    },
    isExpired () {
      return Date.now() >= (this.expiresAt - 60_000) // 1 min antes
    },
    setToken(u: {token:string, refreshToken:string, expiration:string, accessTokenExpiresIn:number}) {
      if (!u.expiration || !u.accessTokenExpiresIn) {
        this.logout()
        return false
      }

      let expiresAt = 0

      if (u.expiration) {
        expiresAt = new Date(u.expiration).getTime()
      }  else {
        // fallback (minutos → ms)
        expiresAt = Date.now() + (u.accessTokenExpiresIn * 60 * 1000)
      }

      const BUFFER = 60 * 1000 // desface de 1 minuto para evitar que el token siga siendo tratado como vigente en el front, cuando puede que ya haya caducado en el servidor.
      const secondsUntilExpire = Math.max(
        0,
        //Math.floor((expiresAt - Date.now()) / 1000)
        Math.floor((expiresAt - Date.now() - BUFFER) / 1000)
      )

      const authToken = useCookie<string | null>('iqa-token', {
        maxAge: secondsUntilExpire,
        secure: true
      })

      const refreshToken = useCookie<string | null>('iqa-refresh-token', {
        //maxAge: 60 * 60 * 24 * 30, // ejemplo 30 días
        secure: true
      })

      const tokenExp = useCookie<number | null>('iqa-token-exp')

      this.token = u.token
      this.refreshToken = u.refreshToken
      this.expiration = u.expiration
      //this.accessTokenExpiresIn = u.accessTokenExpiresIn
      this.expiresAt = expiresAt

      authToken.value = u.token
      refreshToken.value = u.refreshToken
      tokenExp.value = expiresAt

      return true
    },
    logout() {
      //this.user = null
      this.$reset()
      localStorage.removeItem('iqa-auth');
      localStorage.removeItem('iqa-data-entry-tree-cache'); //catálogo de inspecciones
      //localStorage.removeItem('iqa-data-entry-tree-cache-not-for-combobox'); //catálogo de inspecciones
      localStorage.removeItem('iqa-data-entry-tree-cache-records');
      localStorage.removeItem('iqa-data-entry-tree-cache-rex3');
      localStorage.removeItem('iqa-data-entry-tree-cache-open-cell');
      localStorage.removeItem('iqa-data-entry-tree-cache-batches');
      localStorage.removeItem('iqa-data-entry-tree-cache-ai');
      Object.keys(localStorage)
        .filter(key => key.startsWith('catalogo-'))
        .forEach(key => localStorage.removeItem(key)); //remueve todos los catálogos
      //remove cookies
      document.cookie.split(';').forEach(c => {
        document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/')
      })
      const authToken = useCookie('iqa-auth')
      authToken.value = null  // deletes the cookie cleanly in Nuxt
      
      localStorage.clear();
      sessionStorage.clear();
    },
    setCustomer(user: CustomerCredentials,accessToken: string, refreshToken: string, expiration: string, accessTokenExpiresIn: number) {
      this.user = user;
      this.sessionType = 'customer'
      this.token = accessToken
      this.refreshToken = refreshToken
      this.expiration = expiration
      this.customerPermissions = [
        'portal.access'
      ]
      let expiresAt = 0
      if (expiration) {
        expiresAt = new Date(expiration).getTime()
      } else if (accessTokenExpiresIn) {
        expiresAt = Date.now() + (accessTokenExpiresIn * 60 * 1000)
      }
      this.expiresAt = expiresAt

      const authToken = useCookie<string | null>('iqa-token', {
        secure: true
      })
      authToken.value = accessToken

      const refreshTokenCookie = useCookie<string | null>('iqa-refresh-token', {
        secure: true
      })
      refreshTokenCookie.value = refreshToken

      const tokenExp = useCookie<number | null>('iqa-token-exp')
      tokenExp.value = expiresAt
    }
  },
  persist: true,
})